import { useState } from "react";
import { Alert, Button, Input, Modal, Space, Text, useToast } from "@app/ui";
import { api } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";
import { useT } from "../i18n.js";
import { useErrorText } from "../errors/index.js";
import { maskEmail } from "../utils/mask.js";

/**
 * In-app email verification: the user types the 6-digit code we emailed and
 * confirms, or requests a fresh one — no need to leave the app for the link.
 * On success it refreshes the profile so the "verify your email" banner clears.
 */
export function EmailVerifyModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const toast = useToast();
  const errorText = useErrorText();
  const { user, refreshMe } = useAuth();

  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    setCode("");
    setError(null);
    onClose();
  };

  const submit = async () => {
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setError(t("verify.codeInvalid"));
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await api<void>("/auth/verify-email/code", { method: "POST", body: { code: trimmed }, noRetry: true });
      await refreshMe().catch(() => undefined);
      toast.success(t("verify.verified"));
      close();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    setResending(true);
    setError(null);
    try {
      await api<void>("/auth/verify-email/resend", { method: "POST", noRetry: true });
      toast.success(t("verify.resent"));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setResending(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={t("verify.modalTitle")}
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          <Button variant="subtle" loading={resending} onClick={() => void resend()}>
            {t("verify.resend")}
          </Button>
          <Button variant="primary" loading={submitting} onClick={() => void submit()}>
            {t("verify.submit")}
          </Button>
        </div>
      }
    >
      <Space direction="vertical" size="md" style={{ width: "100%" }}>
        {error && <Alert type="error">{error}</Alert>}
        <div>
          <Text small secondary>
            {t("verify.modalBody")}
          </Text>{" "}
          {/* Masked: the address only has to be recognisable, and this modal is
              often open on a shared or screen-shared window. */}
          {user && <Text strong>{maskEmail(user.email)}</Text>}
        </div>
        <Input
          label={t("verify.codeLabel")}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="000000"
          autoFocus
          {...(error ? { error: " " } : {})}
          onKeyDown={(e) => {
            if (e.key === "Enter") void submit();
          }}
        />
      </Space>
    </Modal>
  );
}
